import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ShipperNetwork {
  id: string;
  name: string;
  description: string | null;
  is_default: boolean;
  created_at: string;
  color: string | null;
  member_count?: number;
}

export function useShipperNetworks(userId: string | undefined) {
  const [networks, setNetworks] = useState<ShipperNetwork[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) {
      setNetworks([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    const { data: nets } = await supabase
      .from("shipper_private_networks" as any)
      .select("id, name, description, is_default, created_at, color")
      .eq("shipper_user_id", userId)
      .order("is_default", { ascending: false })
      .order("created_at", { ascending: true });

    const rows = ((nets as any[]) || []) as ShipperNetwork[];
    const ids = rows.map((r) => r.id);

    let counts = new Map<string, number>();
    if (ids.length) {
      const { data: memberRows } = await supabase
        .from("shipper_network_brokers" as any)
        .select("network_id")
        .in("network_id", ids)
        .eq("status", "accepted")
        .is("removed_at", null);
      for (const r of (memberRows as any[]) || []) {
        counts.set(r.network_id, (counts.get(r.network_id) || 0) + 1);
      }
    }
    setNetworks(rows.map((r) => ({ ...r, member_count: counts.get(r.id) || 0 })));
    setLoading(false);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const updateColor = useCallback(async (id: string, color: string | null) => {
    if (!color) return;
    await supabase
      .from("shipper_private_networks" as any)
      .update({ color })
      .eq("id", id);
  }, []);

  const createNetwork = useCallback(
    async (name: string, description?: string | null, isDefault = false, color?: string | null) => {
      const { data, error } = await supabase.rpc("create_shipper_network" as any, {
        p_name: name,
        p_description: description ?? null,
        p_is_default: isDefault,
      });
      if (error) throw error;
      const newId = data as unknown as string;
      if (color && newId) await updateColor(newId, color);
      await load();
      return newId;
    },
    [load, updateColor]
  );

  const updateNetwork = useCallback(
    async (id: string, patch: { name?: string; description?: string | null; is_default?: boolean; color?: string | null }) => {
      const { error } = await supabase.rpc("update_shipper_network" as any, {
        p_network_id: id,
        p_name: patch.name ?? null,
        p_description: patch.description === undefined ? null : patch.description,
        p_is_default: patch.is_default ?? null,
      });
      if (error) throw error;
      if (patch.color) await updateColor(id, patch.color);
      await load();
    },
    [load, updateColor]
  );

  const deleteNetwork = useCallback(
    async (id: string) => {
      const { error } = await supabase.rpc("delete_shipper_network" as any, { p_network_id: id });
      if (error) throw error;
      await load();
    },
    [load]
  );

  const setDefaultNetwork = useCallback(
    async (id: string) => {
      const { error } = await supabase.rpc("set_default_shipper_network" as any, { p_network_id: id });
      if (error) throw error;
      await load();
    },
    [load]
  );

  return { networks, loading, reload: load, createNetwork, updateNetwork, deleteNetwork, setDefaultNetwork };
}
